/**
 * @fileoverview Projects the mock Tenant store onto the backend's Sales CRM, Approvals and
 * prospect metadata shapes.
 * @description The prospect, approval and metadata mocks don't keep a second copy of the banks:
 * they read and write the same tenants-mock-store.ts records the rest of the sales mocks use, and
 * this module translates between the two models — the metadata catalogues (with stable ids for the
 * frontend's bank types, states, core banking solutions and lead sources), a tenant as a backend
 * prospect list row, details or review request, and who the signed-in caller is on the sales roster
 * (read from the shared auth store, the way the real backend reads the access token). Keeping one
 * store means a prospect created through the new endpoints still works with every mock-only row
 * action (edit, archive, assign target, …) and on every other sales page. Never imported outside
 * this feature's mocks/ folder.
 * @copyright Copyright (c) 2026 Brownbox Consulting. All rights reserved.
 * @author Anshul Rokde
 */

import {
  CORE_BANKING_OPTIONS,
  LEAD_SOURCE_OPTIONS,
  STATE_OPTIONS,
  TENANT_BANK_TYPE_OPTIONS,
} from '@/features/sales/constants/sales-options.constants'
import { resolveDownlineUserIds } from '@/features/sales/lib/sales-hierarchy.utils'
import { listSalesRoster } from '@/features/sales/mocks/sales-roster-mock-store'
import { listTargets } from '@/features/sales/mocks/targets-mock-store'
import { useAuthStore } from '@/shared/lib/auth/auth.store'

import type { ApprovalDecisionEntry, ApprovalStatus } from '@/features/sales/types/approval.types'
import type {
  ProspectAdmissionState,
  ProspectDetails,
  ProspectLeadRating,
  ProspectListItem,
  ProspectMetadata,
  ProspectOwner,
  ProspectReference,
  ProspectStageCode,
  ProspectTargetSummary,
  ProspectTier,
} from '@/features/sales/types/prospect.types'
import type { LeadRating } from '@/features/sales/types/sales.types'
import type { BankTier, SalesUserRecord, Target, Tenant } from '@/features/sales/types/tenant.types'

/** @description The tier scheme the mock pins: tier.utils.ts' branch thresholds. */
const TIER_RANGES = [
  { code: 'TIER_1', name: 'Tier 1', minBranches: 15, maxBranches: null },
  { code: 'TIER_2', name: 'Tier 2', minBranches: 8, maxBranches: 14 },
  { code: 'TIER_3', name: 'Tier 3', minBranches: 1, maxBranches: 7 },
] as const

/** @description The stages a prospect moves through, with the labels the metadata publishes. */
const PROSPECT_STAGES: { code: ProspectStageCode; label: string }[] = [
  { code: 'NEW', label: 'New' },
  { code: 'CONTACTED', label: 'Contacted' },
  { code: 'DEMO_REQUESTED', label: 'Demo Requested' },
  { code: 'PROPOSAL_SENT', label: 'Proposal Sent' },
  { code: 'UNDER_NEGOTIATION', label: 'Under Negotiation' },
  { code: 'SUBSCRIPTION_REQUESTED', label: 'Subscription Requested' },
  { code: 'SUBSCRIBED', label: 'Subscribed' },
  { code: 'CLOSED_LOST', label: 'Closed Lost' },
  { code: 'ON_HOLD', label: 'On Hold' },
  { code: 'REJECTED', label: 'Rejected' },
  { code: 'LIVE', label: 'Live' },
]

/** @description The country code every mock contact number uses. */
export const MOCK_COUNTRY_CODE = '+91'

/**
 * @description Builds a stable, UUID-shaped id for one catalogue entry.
 * @param {number} catalogue - Which catalogue (1-4).
 * @param {number} index - The entry's position in it.
 * @returns {string} The id.
 */
function catalogueId(catalogue: number, index: number): string {
  return `00000000-0000-4000-800${String(catalogue)}-${String(index + 1).padStart(12, '0')}`
}

/**
 * @description Turns a display name into a catalogue code (e.g. "Tamil Nadu" -> "TAMIL_NADU").
 * @param {string} name - The display name.
 * @returns {string} The code.
 */
function codeOf(name: string): string {
  return name
    .toUpperCase()
    .replace(/[^A-Z0-9]+/g, '_')
    .replace(/^_|_$/g, '')
}

/**
 * @description Builds one catalogue from its display names.
 * @param {number} catalogue - Which catalogue (1-4), for its ids.
 * @param {readonly string[]} names - Its entries' names.
 * @returns {ProspectReference[]} The catalogue.
 */
function catalogueOf(catalogue: number, names: readonly string[]): ProspectReference[] {
  return names.map((name, index) => ({
    id: catalogueId(catalogue, index),
    code: codeOf(name),
    name,
  }))
}

const BANK_TYPES = catalogueOf(1, TENANT_BANK_TYPE_OPTIONS)
const STATES = catalogueOf(2, STATE_OPTIONS).map((state) => ({ ...state, kind: 'STATE' }))
const CORE_BANKING_SOLUTIONS = catalogueOf(3, CORE_BANKING_OPTIONS)
const LEAD_SOURCES = catalogueOf(4, LEAD_SOURCE_OPTIONS)

/**
 * @description The prospect metadata catalogues, as GET /tenants/prospect-metadata answers them.
 * @returns {ProspectMetadata} Every catalogue and the tier scheme.
 */
export function mockProspectMetadata(): ProspectMetadata {
  return {
    bankTypes: BANK_TYPES,
    coreBankingSolutions: CORE_BANKING_SOLUTIONS,
    states: STATES,
    leadSources: LEAD_SOURCES,
    leadRatings: ['HOT', 'WARM', 'COLD'],
    prospectStages: PROSPECT_STAGES,
    tierScheme: { version: 1, ranges: TIER_RANGES.map((range) => ({ ...range })) },
    metadataVersion: 'metadata-v1.mock',
    evaluatedAt: new Date().toISOString(),
  }
}

/**
 * @description Finds a catalogue entry by name, inventing a stable reference for a name the
 * catalogue doesn't list (an older seed value), so a row never breaks.
 * @param {ProspectReference[]} catalogue - The catalogue.
 * @param {string} name - The entry's name.
 * @returns {ProspectReference} The entry.
 */
function referenceByName(catalogue: ProspectReference[], name: string): ProspectReference {
  return (
    catalogue.find((entry) => entry.name === name) ?? {
      id: catalogueId(
        9,
        [...name].reduce((sum, c) => (sum * 31 + c.charCodeAt(0)) % 1e11, 7),
      ),
      code: codeOf(name),
      name,
    }
  )
}

/**
 * @description A catalogue entry by name, as a reference with its stable id and code.
 * @param {'bankTypes' | 'states' | 'coreBankingSolutions' | 'leadSources'} catalogue - Which one.
 * @param {string} name - The entry's name.
 * @returns {ProspectReference} The entry (a stable one is invented for a name the catalogue lacks).
 */
export function referenceFor(
  catalogue: 'bankTypes' | 'states' | 'coreBankingSolutions' | 'leadSources',
  name: string,
): ProspectReference {
  const all = {
    bankTypes: BANK_TYPES,
    states: STATES,
    coreBankingSolutions: CORE_BANKING_SOLUTIONS,
    leadSources: LEAD_SOURCES,
  }[catalogue]
  return referenceByName(all, name)
}

/**
 * @description Looks up a catalogue entry's name by id.
 * @param {'bankTypes' | 'states' | 'coreBankingSolutions' | 'leadSources'} catalogue - Which one.
 * @param {string} id - The entry's id.
 * @returns {string | undefined} Its name, or undefined for an unknown id.
 */
export function catalogueNameById(
  catalogue: 'bankTypes' | 'states' | 'coreBankingSolutions' | 'leadSources',
  id: string,
): string | undefined {
  const all = {
    bankTypes: BANK_TYPES,
    states: STATES,
    coreBankingSolutions: CORE_BANKING_SOLUTIONS,
    leadSources: LEAD_SOURCES,
  }[catalogue]
  return all.find((entry) => entry.id === id)?.name
}

/**
 * @description The stable id of a state, by name.
 * @param {string} name - The state's name.
 * @returns {string} Its id.
 */
export function stateIdByName(name: string): string {
  return referenceByName(STATES, name).id
}

/**
 * @description The stable id of a bank type, by name.
 * @param {string} name - The bank type's name.
 * @returns {string} Its id.
 */
export function bankTypeIdByName(name: string): string {
  return referenceByName(BANK_TYPES, name).id
}

/**
 * @description The tier a tenant's tier label pins.
 * @param {BankTier} tier - The tenant's tier.
 * @returns {ProspectTier} The pinned tier.
 */
export function tierOf(tier: BankTier): ProspectTier {
  return { schemeVersion: 1, code: codeOf(tier), name: tier }
}

/**
 * @description Converts the frontend's lead rating to the backend's.
 * @param {LeadRating} rating - "Hot", "Warm" or "Cold".
 * @returns {ProspectLeadRating} "HOT", "WARM" or "COLD".
 */
export function toBackendRating(rating: LeadRating): ProspectLeadRating {
  return rating.toUpperCase() as ProspectLeadRating
}

/**
 * @description Converts the backend's lead rating to the frontend's.
 * @param {string} rating - "HOT", "WARM" or "COLD".
 * @returns {LeadRating} "Hot", "Warm" or "Cold".
 */
export function toFrontendRating(rating: string): LeadRating {
  return `${rating.charAt(0)}${rating.slice(1).toLowerCase()}` as LeadRating
}

/**
 * @description The signed-in caller's sales roster record, matched by email the way every sales
 * page matches its viewer.
 * @returns {SalesUserRecord | undefined} The caller, or undefined when they're not on the roster.
 */
export function mockCaller(): SalesUserRecord | undefined {
  const email = useAuthStore.getState().user?.email
  return email ? listSalesRoster().find((user) => user.email === email) : undefined
}

/**
 * @description A roster user as a prospect owner.
 * @param {string | null | undefined} userId - The user's roster id.
 * @returns {ProspectOwner | null} The owner, or null when there's none.
 */
export function ownerOf(userId: string | null | undefined): ProspectOwner | null {
  if (!userId) return null
  const user = listSalesRoster().find((candidate) => candidate.id === userId)
  return {
    userId,
    displayName: user?.name ?? userId,
    employeeCode: userId,
    positionCode: user ? codeOf(user.role) : null,
  }
}

/**
 * @description The reviewer a pending submission is routed to: its maker's Sales Head, or the
 * Super Admin when the reporting line reaches none.
 * @param {Tenant} tenant - The submission.
 * @returns {string} The reviewer's roster id.
 */
export function reviewerOf(tenant: Tenant): string {
  return (
    tenant.approverId ??
    listSalesRoster().find((user) => user.role === 'Super Admin')?.id ??
    'EMP-2000'
  )
}

/**
 * @description A tenant's backend admission state.
 * @param {Tenant} tenant - The tenant.
 * @returns {ProspectAdmissionState} Its admission state.
 */
export function admissionStateOf(tenant: Tenant): ProspectAdmissionState {
  switch (tenant.approval) {
    case 'Pending Approval':
      return 'PENDING_REVIEW'
    case 'Rejected':
      return 'REJECTED'
    case 'Withdrawn':
      return 'WITHDRAWN'
    default:
      return 'ACCEPTED'
  }
}

/** @description The backend's period type for each of the frontend's. */
const BACKEND_PERIOD_TYPES: Record<Target['periodType'], ProspectTargetSummary['periodType']> = {
  Monthly: 'MONTHLY',
  Quarterly: 'QUARTERLY',
  'Half-Yearly': 'BI_ANNUAL',
  Annual: 'ANNUAL',
}

/**
 * @description A tenant's open target, as the list and Details show it.
 * @param {string} tenantId - The tenant.
 * @returns {ProspectTargetSummary | null} Its open target, or null when it is in the backlog.
 */
export function openTargetOf(tenantId: string): ProspectTargetSummary | null {
  const open = listTargets({ tenantId, status: 'Outstanding' })[0]
  if (!open) return null
  return {
    targetId: open.id,
    name: open.name,
    periodType: BACKEND_PERIOD_TYPES[open.periodType],
    periodLabel: open.periodLabel,
    dueDate: open.endDate,
    priority: open.priority.toUpperCase() as ProspectTargetSummary['priority'],
    assigneeUserId: open.ownerId,
  }
}

/**
 * @description A tenant as a backend pipeline row.
 * @param {Tenant} tenant - The tenant.
 * @returns {ProspectListItem} The row.
 */
export function toProspectListItem(tenant: Tenant): ProspectListItem {
  const admissionState = admissionStateOf(tenant)
  return {
    prospectId: tenant.id,
    tenantId: tenant.id,
    tenantCode: tenant.id,
    bankName: tenant.name,
    bankType: referenceByName(BANK_TYPES, tenant.bankType),
    location: {
      stateId: stateIdByName(tenant.state),
      state: tenant.state,
      city: tenant.city,
      district: tenant.district || tenant.city,
    },
    branchCount: tenant.branches,
    tier: tierOf(tenant.tier),
    leadRating: toBackendRating(tenant.leadRating),
    admissionState,
    archivedAt: tenant.archived ? (tenant.archivedAt ?? tenant.createdAt) : null,
    archivedBy: tenant.archived ? ownerOf(tenant.archivedById) : null,
    archiveReason: tenant.archived ? tenant.archiveReason : null,
    createdBy: ownerOf(tenant.createdById),
    currentOwner: ownerOf(tenant.currentOwnerId),
    salesStage: admissionState === 'ACCEPTED' ? 'NEW' : null,
    target: openTargetOf(tenant.id),
    nextFollowUpAt: null,
    createdAt: tenant.createdAt,
  }
}

/** @description Decisions recorded on each review request, by tenant id (mock memory only). */
const decisionsByTenantId = new Map<string, ApprovalDecisionEntry[]>()

/**
 * @description The decisions recorded on a tenant's review request.
 * @param {string} tenantId - The tenant.
 * @returns {ApprovalDecisionEntry[]} Its decisions, oldest first.
 */
export function decisionsOf(tenantId: string): ApprovalDecisionEntry[] {
  return decisionsByTenantId.get(tenantId) ?? []
}

/**
 * @description Records a decision on a tenant's review request.
 * @param {string} tenantId - The tenant.
 * @param {ApprovalDecisionEntry} decision - The decision.
 */
export function recordDecision(tenantId: string, decision: ApprovalDecisionEntry): void {
  decisionsByTenantId.set(tenantId, [...(decisionsByTenantId.get(tenantId) ?? []), decision])
}

/**
 * @description A tenant's review request status.
 * @param {Tenant} tenant - The tenant.
 * @returns {ApprovalStatus} Its status.
 */
export function approvalStatusOf(tenant: Tenant): ApprovalStatus {
  switch (tenant.approval) {
    case 'Pending Approval':
      return 'PENDING'
    case 'Rejected':
      return 'REJECTED'
    case 'Withdrawn':
      return 'CANCELLED'
    default:
      return 'APPROVED'
  }
}

/**
 * @description Whether a tenant ever went through review (an Executive's submission).
 * @param {Tenant} tenant - The tenant.
 * @returns {boolean} True when it has a review request.
 */
export function hasReviewRequest(tenant: Tenant): boolean {
  return (
    (tenant.approval !== undefined && tenant.approval !== 'Approved') ||
    decisionsByTenantId.has(tenant.id)
  )
}

/**
 * @description The review request id of a tenant's submission.
 * @param {string} tenantId - The tenant.
 * @returns {string} The request id.
 */
export function approvalRequestIdOf(tenantId: string): string {
  return `APR-${tenantId}`
}

/**
 * @description The version a decision must send back for a tenant's review request.
 * @param {Tenant} tenant - The tenant.
 * @returns {string} The version.
 */
export function approvalVersionOf(tenant: Tenant): string {
  return `ap1.${tenant.id}.${approvalStatusOf(tenant)}.${String(decisionsByTenantId.get(tenant.id)?.length ?? 0)}`
}

/**
 * @description A tenant's ETag as Get Prospect Details sets it.
 * @param {Tenant} tenant - The tenant.
 * @returns {string} The ETag.
 */
export function prospectEtagOf(tenant: Tenant): string {
  return `"tp1.${tenant.id}.${admissionStateOf(tenant)}"`
}

/**
 * @description A tenant in full, as Get Prospect Details answers it.
 * @param {Tenant} tenant - The tenant.
 * @returns {ProspectDetails} The details.
 */
export function toProspectDetails(tenant: Tenant): ProspectDetails {
  const notes = tenant.notes
    ? [
        {
          noteId: `NOTE-${tenant.id}`,
          text: tenant.notes,
          createdBy: tenant.createdById ?? '',
          createdAt: tenant.createdAt,
        },
      ]
    : []
  const ownershipHistory = tenant.ownerHistory.map((transfer, index) => ({
    ownershipHistoryId: `OWN-${tenant.id}-${String(index + 1)}`,
    fromOwnerUserId: transfer.fromOwnerId,
    toOwnerUserId: transfer.toOwnerId,
    actorUserId: transfer.actingUserId,
    reason: hasReviewRequest(tenant)
      ? ('APPROVAL_ASSIGNMENT' as const)
      : ('INITIAL_ASSIGNMENT' as const),
    createdAt: transfer.transferredAt,
  }))
  return {
    ...toProspectListItem(tenant),
    bank: {
      name: tenant.name,
      bankTypeId: bankTypeIdByName(tenant.bankType),
      registrationNumber: tenant.regNumber,
      headOfficeLocation: tenant.headOffice,
      website: tenant.website || null,
    },
    address: {
      streetAddress: tenant.address,
      stateId: stateIdByName(tenant.state),
      city: tenant.city,
      district: tenant.district || tenant.city,
      pinCode: tenant.pincode,
    },
    organisation: {
      branchCount: tenant.branches,
      employeeCount: tenant.employees,
      coreBankingSolutionId: tenant.coreBanking
        ? referenceByName(CORE_BANKING_SOLUTIONS, tenant.coreBanking).id
        : '',
      existingMisSolution: tenant.existingMis || null,
    },
    primaryContact: {
      name: tenant.authorisedContact.name,
      designation: tenant.authorisedContact.designation || null,
      email: tenant.authorisedContact.email,
      countryCode: MOCK_COUNTRY_CODE,
      mobileNumber: tenant.authorisedContact.phone,
    },
    leadSource: referenceByName(LEAD_SOURCES, tenant.leadSource),
    notes,
    ownershipHistory,
    approval: hasReviewRequest(tenant)
      ? {
          approvalRequestId: approvalRequestIdOf(tenant.id),
          status: approvalStatusOf(tenant),
          routingRevision: 1,
        }
      : null,
    historyTotal: notes.length + ownershipHistory.length,
    historyTruncated: false,
  }
}

/**
 * @description Whether the caller may see an accepted prospect: Super Admin and Sales Head see
 * every one, a Sales Manager those owned within their downline plus unassigned ones they created,
 * a Sales Executive their own.
 * @param {Tenant} tenant - The prospect.
 * @param {SalesUserRecord | undefined} caller - The signed-in caller.
 * @returns {boolean} True when it's in the caller's cohort.
 */
export function isInCallerCohort(tenant: Tenant, caller: SalesUserRecord | undefined): boolean {
  if (!caller) return false
  if (caller.role === 'Super Admin' || caller.role === 'Sales Head') return true
  if (caller.role === 'Sales Executive') return tenant.currentOwnerId === caller.id
  const downline = resolveDownlineUserIds(caller.id, listSalesRoster())
  return tenant.currentOwnerId
    ? downline.includes(tenant.currentOwnerId)
    : tenant.createdById === caller.id
}
